/**
 * Build-time controlled logger for gallery player
 *
 * In PRODUCTION builds (--mode production):
 * - trace(), debug() and info() compile to no-ops (tree-shaken out)
 * - warn() and error() always work
 *
 * In DEV builds (--mode development):
 * - trace() is disabled by default (per-frame noise)
 * - debug(), info(), warn(), error() are active
 * - Set VITE_LOG_TRACE=true to enable trace
 */

// Check if this is a production build (MODE=production, not PROD flag which is always true for vite build)
const IS_PROD_BUILD = import.meta.env.MODE === 'production';
const TRACE_ENABLED = import.meta.env.VITE_LOG_TRACE === 'true';

export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error';

export interface LoggerOptions {
  module: string;
  color?: string;
}

// No-op function for production builds - tree-shaker will eliminate call sites
const noop = (..._args: unknown[]): void => {};

// Module colors for visual distinction in browser console
const moduleColors: Record<string, string> = {
  // Core workers
  slicer: '#4CAF50', // Green
  'slicer:day': '#66BB6A', // Light Green
  'slicer:heat': '#43A047', // Dark Green
  render: '#2196F3', // Blue
  'render-single': '#2196F3', // Blue
  'render:day': '#42A5F5', // Light Blue
  'render:heat': '#1E88E5', // Dark Blue
  // WebGL renderers
  'webgl-composite': '#FF5722', // Deep Orange
  'webgl-simple': '#FF7043', // Light Deep Orange
  // Decoder/playback
  decoder: '#9C27B0', // Purple
  buffer: '#7B1FA2', // Dark Purple
  playback: '#AB47BC', // Light Purple
  // Player components
  player: '#673AB7', // Deep Purple
  'osd-manager': '#E91E63', // Pink
  osd: '#FF6090', // Light Pink (OSD worker)
  // Data/API
  parser: '#795548', // Brown
  api: '#00BCD4', // Cyan
  'video-meta': '#0097A7', // Dark Cyan
  // UI
  gallery: '#E91E63', // Pink
  store: '#F06292', // Light Pink
  timeline: '#FF9800', // Orange
  fetcher: '#607D8B', // Gray
  // Export
  exporter: '#009688', // Teal
};

function getModuleColor(module: string): string {
  return moduleColors[module.toLowerCase()] || '#888';
}

export interface Logger {
  trace: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

/**
 * Create a logger instance for a module.
 *
 * In production builds, debug/info are no-ops that get tree-shaken.
 * In dev builds, all log levels are active.
 */
/**
 * Create a stream-scoped logger for worker contexts.
 * Builds a module name like "slicer:day" or "render:heat" for easy console filtering.
 */
export function createStreamLogger(worker: string, streamId: string): Logger {
  return createLogger({ module: `${worker}:${streamId}` });
}

export function createLogger(options: LoggerOptions): Logger {
  const { module } = options;
  const color = options.color || getModuleColor(module);
  const prefix = `[${module}]`;

  // Check if running in worker (no window object)
  const isWorker = typeof window === 'undefined';

  const formatPrefix = isWorker ? prefix : `%c${prefix}`;
  const getStyle = () => `color: ${color}; font-weight: bold`;

  // Production build: trace/debug/info are no-ops (tree-shaker eliminates them)
  if (IS_PROD_BUILD) {
    return {
      trace: noop,
      debug: noop,
      info: noop,
      warn: (...args: unknown[]) => {
        if (isWorker) {
          console.warn(formatPrefix, ...args);
        } else {
          console.warn(formatPrefix, getStyle(), ...args);
        }
      },
      error: (...args: unknown[]) => {
        if (isWorker) {
          console.error(formatPrefix, ...args);
        } else {
          console.error(formatPrefix, getStyle(), ...args);
        }
      },
    };
  }

  // Dev build: trace disabled by default, debug/info/warn/error active
  return {
    trace: TRACE_ENABLED
      ? (...args: unknown[]) => {
          if (isWorker) {
            console.log(formatPrefix, ...args);
          } else {
            console.log(formatPrefix, getStyle(), ...args);
          }
        }
      : noop,

    debug: (...args: unknown[]) => {
      if (isWorker) {
        console.log(formatPrefix, ...args);
      } else {
        console.log(formatPrefix, getStyle(), ...args);
      }
    },

    info: (...args: unknown[]) => {
      if (isWorker) {
        console.info(formatPrefix, ...args);
      } else {
        console.info(formatPrefix, getStyle(), ...args);
      }
    },

    warn: (...args: unknown[]) => {
      if (isWorker) {
        console.warn(formatPrefix, ...args);
      } else {
        console.warn(formatPrefix, getStyle(), ...args);
      }
    },

    error: (...args: unknown[]) => {
      if (isWorker) {
        console.error(formatPrefix, ...args);
      } else {
        console.error(formatPrefix, getStyle(), ...args);
      }
    },
  };
}
